<template>
  <div
    v-if="show"
    :class="[
      $style['ns-feature'],
      { [$style['right-align']]: textAreaAlignment === 'right' },
      { [$style['half-image']]: imageSize === 'half' }
    ]"
    :style="{ 'background-color': imageSize === 'half' && halfBackgroundColor }"
  >
    <div
      :class="$style['feature-image']"
      :style="{ 'background-image': `url(${imageSrc})` }"
    ></div>
    <div :class="$style['feature-block']">
      <div :class="$style['feature-text-area']">
        <div :class="$style['fta-title']">{{ title }}</div>
        <div
          v-if="description"
          :class="$style['fta-description']"
          v-html="description"
        ></div>
        <div v-if="ctaVisible" :class="$style['fta-call-to-action']">
          <button
            v-if="ctaType === 'button' || isMobile"
            class="btn-primary primary-outline"
            @click.stop="ctaClicked"
          >
            {{ ctaText }}
          </button>
          <NsExpressiveLink v-else @click="ctaClicked">
            {{ ctaText }}
          </NsExpressiveLink>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import "@nuskin/ns-core-styles/src/dist/main.css";

import { NsExpressiveLink } from "@nuskin/design-components";

export default {
  name: "NsFeature",
  components: {
    NsExpressiveLink
  },
  props: {
    /**
     * TItle text
     */
    title: {
      type: String,
      default: ""
    },
    /**
     * Description text
     */
    description: {
      type: String,
      default: ""
    },
    /**
     * Text of the call to action button
     */
    ctaText: {
      type: String,
      default: ""
    },
    /**
     * Url of the call to action button
     */
    ctaUrl: {
      type: String,
      default: ""
    },
    /**
     * If true, will open the link in a new tab
     */
    ctaOpensNewTab: Boolean,
    /**
     * Detirmines whether the call to action is a button or an expressive link
     * @values button, expressive-link
     */
    ctaType: {
      type: String,
      validator: prop => ["button", "expressive-link"].includes(prop),
      default: "button"
    },
    /**
     * Src url of the background image
     */
    imageSrc: {
      type: String,
      default: ""
    },
    /**
     * Detirmines whether the image will take up the full background or half
     * @values full, half
     */
    imageSize: {
      type: String,
      validator: prop => ["half", "full"].includes(prop),
      default: "full"
    },
    /**
     * Detirmines whether the text box will be on the left or right side
     * @values left, right
     */
    textAreaAlignment: {
      type: String,
      validator: prop => ["left", "right"].includes(prop),
      default: "left"
    },
    /**
     * Sets the background color behind the textbox when imageSize is set to 'half'
     */
    halfBackgroundColor: {
      type: String,
      default: "white"
    }
  },
  computed: {
    show() {
      return (
        !!this.title || !!this.description || !!this.ctaText || !!this.imageSrc
      );
    },
    isMobile() {
      return this.$mq === "mobile" || this.$mq === "phablet";
    },
    ctaVisible() {
      return !!this.ctaUrl && !!this.ctaText;
    }
  },
  methods: {
    ctaClicked() {
      window.open(this.ctaUrl, this.ctaOpensNewTab ? "_blank" : "_top");
    }
  }
};
</script>

<style module lang="scss">
@use "sass:map";
@use "~@nuskin/ns-core-styles/src/scss/colors";
@use "~@nuskin/ns-core-styles/src/scss/other";
@use '~@nuskin/ns-core-styles/src/scss/global.mixins';
@use '~@nuskin/ns-core-styles/src/scss/typographyVariables';
@use "~@nuskin/ns-core-styles/src/scss/globalVariables";

.ns-feature {
  position: relative;
  display: flex;
  width: 100%;
  height: auto;
  text-align: left;
  background-color: white;

  .feature-block {
    display: flex;
    z-index: 2;
  }

  .feature-text-area {
    box-sizing: border-box;
    width: 357px;
    margin: 29px 30px 27px 30px;
    padding: 29px 40px 27px 40px;
    font-family: map.get(typographyVariables.$typography, font-family-primary);
    background-color: white;

    .fta-title {
      font-style: italic;
      font-weight: bold;
      font-size: 20px;
      line-height: 26px;
      letter-spacing: 1.5px;
      text-transform: uppercase;
      color: #332f20;
    }

    .fta-description {
      margin-top: 15px;
      font-style: normal;
      font-weight: 300;
      font-size: 16px;
      line-height: 22px;
      color: #444444;
    }

    .fta-call-to-action {
      margin-top: 22px;

      button,
      a {
        box-sizing: content-box;
        min-height: 17px;
        line-height: 1;
        cursor: pointer;
      }

      button {
        box-sizing: border-box;
        width: 100%;
        text-align: center;
        font-size: 14px;
        font-weight: map.get(
          typographyVariables.$typography,
          font-weight-semibold
        );
        letter-spacing: 1.08px;
        text-transform: uppercase;
      }

      a {
        text-decoration: none;
        font-style: normal;
        font-size: 15px;
        font-weight: 600;
        letter-spacing: 0.576923px;
        color: #332f20;

        transition: map.get(other.$otherUtils, global-transition);
        border-bottom: 1px solid transparent;

        &:hover {
          border-bottom: 1px solid currentColor;
        }
      }
    }
  }

  .feature-image {
    position: absolute;
    width: 100%;
    height: 100%;
    background: white;
    background-repeat: no-repeat;
    background-size: cover;
    background-position: right;
    z-index: 1;
  }

  &.right-align {
    justify-content: flex-end;

    .feature-image {
      background-position: left;
    }
  }

  &.half-image {
    .feature-block {
      width: 50%;
      justify-content: center;
    }

    .feature-image {
      width: 50%;
      right: 50%;
      background-position: center;
    }
  }

  &.half-image:not(.right-align) {
    .feature-image {
      left: 50%;
    }
  }
}

@media (max-width: 767px) {
  .ns-feature {
    display: block;

    .feature-image {
      position: relative;
      height: 225px;
    }

    .feature-block {
      width: 100%;
    }

    .feature-text-area {
      width: 100%;
      margin: 0;
      padding: 25px 15px 30px;
    }

    &.right-align {
      justify-content: center;
    }

    &.half-image {
      .feature-block {
        width: 100%;
      }

      .feature-image {
        width: 100%;
        right: unset;
      }
    }

    &.half-image:not(.right-align) {
      .feature-image {
        left: unset;
      }
    }
  }
}
</style>
